import { useState, useCallback, useEffect } from 'react';
import { useGoogleLogin, CodeResponse } from '@react-oauth/google';
import { api } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { getGoogleRedirectUri } from '../utils/apiConfig';

interface GoogleAuthResponse {
    action: 'signin' | 'signup';
    result?: any;
    profile?: any;
}

interface UseGoogleAuthOptions {
    onSuccess?: (response: GoogleAuthResponse) => void;
    onError?: (error: any) => void;
}

export function useGoogleAuth(options?: UseGoogleAuthOptions) {
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [isWaitingForCallback, setIsWaitingForCallback] = useState(false);

    const handleSuccess = useCallback(
        async (codeResponse: CodeResponse) => {
            console.log('[PAIR-APP] Google OAuth success, received code');
            console.log('[PAIR-APP] Code response:', codeResponse);
            setIsLoading(true);

            try {
                // First, try to sign in with the code using V2 endpoint
                console.log('[PAIR-APP] Calling socialSignInV2...');
                const signInResult = await api.socialSignInV2(
                    'google',
                    codeResponse.code
                );

                if (signInResult.success && signInResult.data) {
                    console.log(
                        '[PAIR-APP] Google Sign-In V2 successful for existing user'
                    );
                    console.log(
                        '[PAIR-APP] signInResult from socialSignInV2:',
                        {
                            success: signInResult.success,
                            hasData: !!signInResult.data,
                            hasToken: !!signInResult.data?.token,
                            tokenLength: signInResult.data?.token
                                ? signInResult.data.token.length
                                : 0,
                            dataKeys: signInResult.data
                                ? Object.keys(signInResult.data)
                                : [],
                        }
                    );
                    console.log(
                        '[PAIR-APP] Full signInResult.data:',
                        signInResult.data
                    );

                    const response: GoogleAuthResponse = {
                        action: 'signin',
                        result: signInResult,
                    };
                    options?.onSuccess?.(response);
                    return;
                }

                // If sign in failed (user doesn't exist), use the profile data from the response
                if (
                    !signInResult.success &&
                    signInResult.requiresSignup &&
                    signInResult.profile
                ) {
                    console.log(
                        '[PAIR-APP] User not found, using profile data from sign-in response'
                    );
                    console.log(
                        '[PAIR-APP] Profile data:',
                        signInResult.profile
                    );

                    const response: GoogleAuthResponse = {
                        action: 'signup',
                        profile: {
                            success: true,
                            data: signInResult.profile,
                        },
                    };
                    options?.onSuccess?.(response);
                } else if (!signInResult.success) {
                    // Some error occurred
                    console.error(
                        '[PAIR-APP] Sign in failed:',
                        signInResult.error
                    );
                    showToast(
                        signInResult.error || 'Google authentication failed',
                        'error'
                    );
                    options?.onError?.(
                        new Error(
                            signInResult.error || 'Google authentication failed'
                        )
                    );
                }
            } catch (error) {
                console.error('[PAIR-APP] Google auth error:', error);
                showToast('Google authentication failed', 'error');
                options?.onError?.(error);
            } finally {
                setIsLoading(false);
            }
        },
        [showToast, options]
    );

    const handleError = useCallback(() => {
        console.log('[PAIR-APP] Google Sign-In was cancelled or failed');
        setIsWaitingForCallback(false);
        showToast('Google Sign-In was cancelled', 'error');
        options?.onError?.(new Error('Google Sign-In was cancelled'));
    }, [showToast, options]);

    const redirectUri = getGoogleRedirectUri();

    // Listen for localStorage changes as a fallback for COOP-blocked popups
    useEffect(() => {
        if (!isWaitingForCallback) return;

        const checkLocalStorage = () => {
            const code = localStorage.getItem('google_oauth_code');
            const timestamp = localStorage.getItem('google_oauth_timestamp');

            if (code && timestamp) {
                const age = Date.now() - parseInt(timestamp, 10);
                // Only process if the code is less than 5 seconds old
                if (age < 5000) {
                    console.log(
                        '[PAIR-APP] Found OAuth code in localStorage, processing...'
                    );

                    // Clear the localStorage items
                    localStorage.removeItem('google_oauth_code');
                    localStorage.removeItem('google_oauth_timestamp');

                    // Process the code
                    handleSuccess({ code } as CodeResponse);
                    setIsWaitingForCallback(false);
                }
            }
        };

        // Check immediately
        checkLocalStorage();

        // Set up an interval to check localStorage
        const interval = setInterval(checkLocalStorage, 500);

        // Also listen for storage events (won't fire in same window, but good practice)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'google_oauth_code') {
                checkLocalStorage();
            }
        };
        window.addEventListener('storage', handleStorageChange);

        // Clean up after 30 seconds
        const timeout = setTimeout(() => {
            setIsWaitingForCallback(false);
            console.log('[PAIR-APP] OAuth callback timeout');
        }, 30000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [isWaitingForCallback, handleSuccess]);

    // Remove logs that run on every render - they're now in the debugGoogleLogin function

    const googleLogin = useGoogleLogin({
        flow: 'auth-code',
        ux_mode: 'popup',
        redirect_uri: redirectUri, // This needs to match what the backend expects
        onSuccess: handleSuccess,
        onError: handleError,
        scope: 'email profile',
    });

    // Wrap googleLogin to add debugging
    const debugGoogleLogin = () => {
        // Log the Google Client ID being used
        const { getGoogleClientId } = require('../utils/apiConfig');
        const clientId = getGoogleClientId();

        console.log('========================================');
        console.log('[PAIR-APP] 🔐 INITIATING GOOGLE OAUTH');
        console.log('[PAIR-APP] Client ID being used:', clientId);
        console.log(
            '[PAIR-APP] Is V2 Client?:',
            clientId.startsWith('629091735397') ? '✅ YES (V2)' : '⚠️ NO (V1)'
        );
        console.log('[PAIR-APP] Redirect URI:', redirectUri);
        console.log('[PAIR-APP] Window origin:', window.location.origin);
        console.log('========================================');

        // Set waiting flag before opening popup
        setIsWaitingForCallback(true);

        googleLogin();
    };

    return {
        googleLogin: debugGoogleLogin,
        isLoading: isLoading || isWaitingForCallback,
    };
}
